import { BUY_RECORDS } from './buy-records';
import type { Brief } from './schema';
import { PROGRAMS, type ProgramInfo } from './valuations';

/**
 * The best points-buying deals across all the briefs: every item priced "¢/pt to buy", matched
 * to its program's usual value, still running, best value for money first.
 */

export interface BuyDeal {
  key: string;
  title: string;
  emoji: string | null;
  /** What a point costs in this sale, in cents. */
  costCpp: number;
  /** What the program's points are usually worth (valuation, cents). */
  valueCpp: number | null;
  program: ProgramInfo | null;
  /** Value over cost: 1.5 means each dollar buys $1.50 of typical value. */
  ratio: number | null;
  endsOn: string | null;
  dates: string | null;
  why: string | null;
  source: string | null;
  url: string | null;
  /** The brief it came from. */
  briefId: string;
  seenOn: string;
  /** The fewest and most points you can buy in the deal, when the source says. */
  minPoints: number | null;
  maxPoints: number | null;
  /** The lowest price this currency has ever sold for, to judge the deal against. */
  bestEver: BestEver | null;
}

export interface BestEver {
  /** Cents per point. */
  cpp: number;
  /** YYYY-MM, or YYYY when only the year is known. */
  when: string | null;
  /** Like "120% bonus". */
  promo: string | null;
  /** A published record low, or the lowest price in my own briefs. */
  from: 'record' | 'briefs';
  /** When a brief beat the published record: what the record was. */
  beat: number | null;
}

/** Currencies that sell but aren't in the valuation table, by a word in the deal's title. */
const OTHER_SELLERS: [string, string][] = [
  ['starlux', 'starlux'],
  ['cosmile', 'starlux'],
  ['etihad', 'etihad'],
  ['asia miles', 'asiamiles'],
  ['cathay', 'asiamiles'],
];

/** Which currency a sale is for: a programId, another seller's key, or null. */
export function saleKey(title: string): string | null {
  const t = title.toLowerCase();
  return programFor(title)?.id ?? OTHER_SELLERS.find(([w]) => t.includes(w))?.[1] ?? null;
}

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

/** "0.45¢/pt to buy" → 0.45; null when it isn't a buying price. */
export function buyCpp(cpp: string | undefined | null): number | null {
  if (!cpp || !/to buy|buy/i.test(cpp)) return null;
  const m = /(\d+(?:\.\d+)?)\s*¢/.exec(cpp) ?? /(\d+(?:\.\d+)?)\s*(?:c|cents)\b/i.exec(cpp);
  return m ? Number(m[1]) : null;
}

/** "Posted Oct 7 · ⏳ Ends Oct 10" (brief from 2026-10-08) → "2026-10-10". */
export function endDate(dates: string | undefined | null, briefDate: string): string | null {
  // "Ends Oct 10", "Ends tonight, Oct 13", "Ends Oct 19, 4:59pm ET".
  const m =
    /ends?\s+(?:(?:tonight|today|tomorrow|soon)[,\s]+)?([A-Za-z]{3})[a-z]*\.?\s+(\d{1,2})(?:,\s+(\d{4}))?/i.exec(
      dates ?? '',
    );
  if (!m?.[1] || !m[2]) return null;
  const month = MONTHS.indexOf(m[1].toLowerCase());
  if (month < 0) return null;
  let year = m[3] ? Number(m[3]) : Number(briefDate.slice(0, 4));
  const iso = (y: number) =>
    `${y}-${String(month + 1).padStart(2, '0')}-${String(Number(m[2])).padStart(2, '0')}`;
  // An end in January written in a December brief is next year's.
  if (!m[3] && iso(year) < briefDate && Number(briefDate.slice(5, 7)) - (month + 1) > 6) year++;
  return iso(year);
}

/** The program a deal is about, from its title ("Hyatt", "Avios", "Aeroplan"…). */
export function programFor(title: string): ProgramInfo | null {
  const t = title.toLowerCase();
  const words: [string, string][] = [
    ['membership rewards', 'amex_mr'],
    ['ultimate rewards', 'chase_ur'],
    ['thankyou', 'citi_ty'],
    ['capital one', 'capital_one'],
    ['bilt', 'bilt'],
    ['marriott', 'marriott'],
    ['bonvoy', 'marriott'],
    ['hyatt', 'hyatt'],
    ['hilton', 'hilton'],
    ['ihg', 'ihg'],
    ['wyndham', 'wyndham'],
    ['choice', 'choice'],
    ['accor', 'accor'],
    ['delta', 'delta'],
    ['skymiles', 'delta'],
    ['aadvantage', 'american'],
    ['american', 'american'],
    ['united', 'united'],
    ['mileageplus', 'united'],
    ['southwest', 'southwest'],
    ['alaska', 'alaska'],
    ['atmos', 'alaska'],
    ['jetblue', 'jetblue'],
    ['aeroplan', 'aeroplan'],
    ['flying blue', 'flyingblue'],
    ['avios', 'avios'],
    ['british airways', 'avios'],
    ['qatar', 'avios'],
    ['iberia', 'avios'],
    ['virgin', 'virgin'],
    ['emirates', 'emirates'],
    ['krisflyer', 'krisflyer'],
    ['turkish', 'turkish'],
  ];
  const hit = words.find(([w]) => t.includes(w));
  return hit ? (PROGRAMS.find((p) => p.id === hit[1]) ?? null) : null;
}

/** Deals still running on `today` (undated ones for two weeks after they were seen). */
export function buyDeals(briefs: Brief[], today: string): BuyDeal[] {
  const byKey = new Map<string, BuyDeal>();
  // The cheapest each currency has been in any brief, running or not.
  const lowest = new Map<string, { cpp: number; on: string }>();
  const sorted = [...briefs].filter((b) => !b.deleted).sort((a, b) => a.date.localeCompare(b.date));
  for (const b of sorted) {
    for (const section of b.sections) {
      for (const item of section.items ?? []) {
        const cost = buyCpp(item.cpp);
        if (cost == null || cost <= 0) continue;
        const program = programFor(item.title);
        const ends = endDate(item.dates, b.date);
        const seller = saleKey(item.title) ?? item.title.toLowerCase().slice(0, 40);
        const low = lowest.get(seller);
        if (!low || cost < low.cpp) lowest.set(seller, { cpp: cost, on: b.date });
        // One card per sale: briefs often price the same sale a little differently (1.38¢ and
        // "about 1.4¢"), so a sale is its currency and end date; the newest copy wins.
        const key = `${seller}:${ends ?? cost}`;
        const value = program?.typicalCpp ?? null;
        byKey.set(key, {
          key,
          title: item.title,
          emoji: item.emoji ?? null,
          costCpp: cost,
          valueCpp: value,
          program,
          ratio: value != null ? Math.round((value / cost) * 100) / 100 : null,
          endsOn: ends,
          dates: item.dates ?? null,
          why: item.why ?? null,
          source: item.source ?? null,
          url: item.url ?? null,
          briefId: b.id,
          seenOn: b.date,
          // A later copy without limits keeps the ones an earlier copy had.
          minPoints: item.minPoints ?? byKey.get(key)?.minPoints ?? null,
          maxPoints: item.maxPoints ?? byKey.get(key)?.maxPoints ?? null,
          bestEver: null,
        });
      }
    }
  }
  const twoWeeksAfter = (d: string) =>
    new Date(Date.parse(`${d}T12:00:00Z`) + 14 * 86_400_000).toISOString().slice(0, 10);
  return [...byKey.values()]
    .filter((d) => (d.endsOn ?? twoWeeksAfter(d.seenOn)) >= today)
    .map((d) => {
      const seller = d.key.slice(0, d.key.lastIndexOf(':'));
      const record = BUY_RECORDS[seller];
      const low = lowest.get(seller);
      const fromRecord: BestEver | null =
        record?.cpp != null
          ? { cpp: record.cpp, when: record.when, promo: record.promo, from: 'record', beat: null }
          : null;
      const fromBriefs: BestEver | null = low
        ? {
            cpp: low.cpp,
            when: low.on.slice(0, 7),
            promo: null,
            from: 'briefs',
            beat: fromRecord && low.cpp < fromRecord.cpp ? fromRecord.cpp : null,
          }
        : null;
      // A brief can beat the published record (a newer sale); otherwise the record wins.
      const bestEver =
        fromRecord && fromBriefs
          ? fromBriefs.cpp < fromRecord.cpp
            ? fromBriefs
            : fromRecord
          : (fromRecord ?? fromBriefs);
      return { ...d, bestEver };
    })
    .sort((a, b) => (b.ratio ?? 0) - (a.ratio ?? 0) || a.costCpp - b.costCpp);
}
